/**
 * Dynamic Uniform Variant Selector
 *
 * On products tagged "dynamic-uniform-selection", hides the target option (Size) while the
 * controller option ("Already have a uniform?") is set to the hide value ("Yes").
 *
 * The theme fetches and swaps the whole <variant-selects> element on every option change,
 * so nothing here may rely on nodes or listeners inside it surviving. State lives as a
 * data attribute on <product-info> and the hiding itself is done in CSS.
 */

class DynamicUniformSelector {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.controllerOption = productInfo.dataset.uniformControllerOption;
    this.targetOption = productInfo.dataset.uniformTargetOption;
    this.hideValue = (productInfo.dataset.uniformHideValue || '').toLowerCase();
    this.syncTimeout = null;

    if (!this.controllerOption || !this.targetOption || !this.hideValue) return;

    this.sync();

    // Delegated on document so the listener outlives every <variant-selects> swap.
    document.addEventListener('change', (event) => {
      if (this.productInfo.contains(event.target)) this.scheduleSync();
    });

    // The swapped-in markup arrives asynchronously; re-read the selection once it lands.
    new MutationObserver(() => this.scheduleSync()).observe(this.productInfo, {
      childList: true,
      subtree: true,
    });
  }

  scheduleSync() {
    clearTimeout(this.syncTimeout);
    this.syncTimeout = setTimeout(() => this.sync(), 50);
  }

  sync() {
    const selected = this.getSelectedValue(this.controllerOption);
    if (selected === null) return;

    const shouldHide = selected.toLowerCase() === this.hideValue;

    if (shouldHide) {
      this.productInfo.dataset.uniformHidden = 'true';
    } else {
      delete this.productInfo.dataset.uniformHidden;
    }
  }

  getSelectedValue(optionName) {
    const container = Array.from(this.productInfo.querySelectorAll('[data-option-name]')).find(
      (element) => element.dataset.optionName === optionName
    );
    if (!container) return null;

    const checked = container.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;

    const select = container.querySelector('select');
    return select ? select.value : null;
  }
}

document.querySelectorAll('product-info[data-dynamic-uniform-selection="true"]').forEach((productInfo) => {
  new DynamicUniformSelector(productInfo);
});
